/**
 * TextWidget - Display rich text content within portal pages
 *
 * Supports:
 * - Multi-language content (translatable strings)
 * - MUI typography variants
 * - User-editable mode (when allowed by portal config)
 * - Markdown-lite rendering (bold, italic, links)
 * - Custom alignment and styling
 */

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, TextField, IconButton, Tooltip, alpha } from "@mui/material";
import {
  Edit as EditIcon,
  Check as SaveIcon,
  Close as CancelIcon,
} from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import { useMaterialUIController } from "context";

/**
 * Parse simple markdown-lite syntax:
 * **bold**, *italic*, [text](url)
 */
function renderMarkdownLite(text) {
  if (!text) return null;

  const parts = [];
  const remaining = text;
  let key = 0;

  // Process in order: links, bold, italic
  const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(remaining)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      parts.push(remaining.substring(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      // Link: [text](url)
      parts.push(
        <a
          key={key++}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      // Bold: **text**
      parts.push(
        <strong key={key++}>{match[4]}</strong>
      );
    } else if (match[5]) {
      // Italic: *text*
      parts.push(
        <em key={key++}>{match[5]}</em>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last match
  if (lastIndex < remaining.length) {
    parts.push(remaining.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function TextWidget({
  content,
  variant = "body1",
  align = "left",
  color,
  sx = {},
  userEditable = false,
  onContentChange,
  language = "en",
  fallbackLanguage = "en",
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  // Resolve content from translatable object or string
  const resolvedContent = (() => {
    if (!content) return "";
    if (typeof content === "string") return content;
    if (typeof content === "object") {
      return content[language] || content[fallbackLanguage] || content[Object.keys(content)[0]] || "";
    }
    return String(content);
  })();

  const handleStartEdit = useCallback(() => {
    if (!userEditable) return;
    setEditValue(resolvedContent);
    setIsEditing(true);
  }, [userEditable, resolvedContent]);

  const handleSave = useCallback(() => {
    setIsEditing(false);
    if (onContentChange) {
      onContentChange(editValue, language);
    }
  }, [editValue, language, onContentChange]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue("");
  }, []);

  const textColor = color || (darkMode ? "white" : "rgba(0, 0, 0, 0.87)");

  if (isEditing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 2,
          height: "100%",
          ...sx,
        }}
      >
        <TextField
          multiline
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          variant="outlined"
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              color: darkMode ? "white" : "rgba(0,0,0,0.87)",
              "& fieldset": {
                borderColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
              },
              "&:hover fieldset": {
                borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
              },
            },
          }}
        />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <IconButton size="small" onClick={handleCancel} sx={{ color: "#EF4444" }}>
            <CancelIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleSave} sx={{ color: "#10B981" }}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: variant.startsWith("h") ? "center" : "flex-start",
        p: 2,
        "&:hover .text-edit-btn": {
          opacity: userEditable ? 1 : 0,
        },
        ...sx,
      }}
    >
      <KDTypography
        variant={variant}
        sx={{
          color: textColor,
          textAlign: align,
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {renderMarkdownLite(resolvedContent)}
      </KDTypography>

      {userEditable && (
        <Tooltip title="Edit text">
          <IconButton
            className="text-edit-btn"
            size="small"
            onClick={handleStartEdit}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "opacity 0.2s ease",
              color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
              bgcolor: darkMode
                ? alpha("#fff", 0.05)
                : alpha("#000", 0.05),
              "&:hover": {
                bgcolor: darkMode
                  ? alpha("#fff", 0.1)
                  : alpha("#000", 0.1),
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

TextWidget.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  variant: PropTypes.string,
  align: PropTypes.oneOf(["left", "center", "right", "justify"]),
  color: PropTypes.string,
  sx: PropTypes.object,
  userEditable: PropTypes.bool,
  onContentChange: PropTypes.func,
  language: PropTypes.string,
  fallbackLanguage: PropTypes.string,
};

export default TextWidget;
